import { useEffect, useState } from "react";
import { PDFDocument } from "pdf-lib";

export const usePDF = () => {
  const [scale, setScale] = useState(1.5);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [signedPdfUrl, setSignedPdfUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    const updateScale = () => {
      const width = window.innerWidth;

      if (width <= 768) {
        setScale(0.5); // Mobile
      } else if (width <= 1024) {
        setScale(1); // Tablet
      } else {
        setScale(1.5); // Desktop
      }
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const generateSignedPdf = async (pdfUrl, signaturePositions) => {
    if (!pdfUrl) return;

    setIsGeneratingPdf(true);
    try {
      const encodedUrl = encodeURI(pdfUrl);
      const arrayBuffer = await fetch(encodedUrl).then((res) => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(arrayBuffer, {
        ignoreEncryption: true,
      });

      for (const position of signaturePositions) {
        if (!position.signature) continue;

        const page = pdfDoc.getPages()[position.page - 1];
        const { width: pdfWidth, height: pdfHeight } = page.getSize();

        let signatureImage;
        if (position.signature.startsWith("data:image/png")) {
          signatureImage = await pdfDoc.embedPng(position.signature);
        } else if (
          position.signature.startsWith("data:image/jpeg") ||
          position.signature.startsWith("data:image/jpg")
        ) {
          signatureImage = await pdfDoc.embedJpg(position.signature);
        }

        const signatureWidth = 150;
        const signatureHeight = 50;

        const xPos = position.normalizedX * pdfWidth - signatureWidth / 2;
        const yPos =
          pdfHeight - position.normalizedY * pdfHeight - signatureHeight / 2;

        page.drawImage(signatureImage, {
          x: xPos,
          y: Math.max(0, Math.min(yPos, pdfHeight - signatureHeight)),
          width: signatureWidth,
          height: signatureHeight,
        });

        if (position.timestamp) {
          page.drawText(position.timestamp, {
            x: xPos + 15,
            y: Math.max(0, Math.min(yPos, pdfHeight - signatureHeight)),
            size: 8,
          });
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const signedPdf = new File([blob], "signed.pdf", {
        type: "application/pdf",
      });
      setSignedPdfUrl(url);
      return signedPdf;
    } catch (error) {
      console.error("Error generating signed PDF:", error);
      alert("Error generating signed PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return {
    scale,
    setScale,
    currentPage,
    setCurrentPage,
    numPages,
    signedPdfUrl,
    isGeneratingPdf,
    onDocumentLoadSuccess,
    generateSignedPdf,
    setSignedPdfUrl,
  };
};
